<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <page-heading>Reports</page-heading>

      <reports-filter-bar
        :years="filterState.years"
        :year="filterState.year"
        :drafts="filterState.drafts"
        :draft-id="filterState.draftId"
        :drafts-loading="filterState.draftsLoading"
        active="incoming"
        @year-change="onYearChange"
        @draft-change="onDraftChange"
      />

      <template v-if="filterState.draftId != null">
        <div class="d-flex flex-wrap align-items-end mb-3">
          <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-2 mr-3">
            <div class="filter">
              <div class="form-check pl-0" v-for="t in commitFilters" :key="t.id">
                <input
                  class="form-check-input"
                  type="radio"
                  :name="'paymentFilter'"
                  :id="'paymentFilter_' + t.id"
                  :value="t.id"
                  :checked="filters.commit === t.id"
                  @change="setCommit(t.id)"
                />
                <label class="form-check-label" :for="'paymentFilter_' + t.id">{{ t.label }}</label>
              </div>
            </div>
          </div>

          <div class="form-group ml-auto mb-2">
            <div class="input-append d-flex">
              <input
                v-model="searchInput"
                type="text"
                class="form-control mb-0"
                placeholder="Search..."
                style="min-width: 180px"
                @keyup.enter="setSearch(searchInput)"
              />
              <div class="filter">
                <div class="form-check pl-2 mb-0">
                  <input class="form-check-input" type="radio" id="searchBtn" style="display:none" />
                  <label class="form-check-label font-weight-normal" for="searchBtn" style="cursor:pointer" @click="setSearch(searchInput)">Search</label>
                </div>
                <div class="form-check pl-2 mb-0">
                  <input class="form-check-input" type="radio" id="resetBtn" style="display:none" />
                  <label class="form-check-label font-weight-normal" for="resetBtn" style="cursor:pointer" @click="searchInput = ''; resetSearch()">Reset</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex mb-3">
          <app-button variant="outline-primary" size="sm" class="mr-2" :block="false" :loading="exportingFormat === 'pdf'" :disabled="exportingFormat != null" @click="exportReport('pdf')">Download PDF</app-button>
          <app-button variant="outline-primary" size="sm" :block="false" :loading="exportingFormat === 'csv'" :disabled="exportingFormat != null" @click="exportReport('csv')">Download CSV</app-button>
        </div>

        <div class="position-relative" style="min-height: 300px">
          <grid-table
            :columns="incomingColumns"
            :rows="items"
            :loading="loading"
            :sort="filters.sort"
            :direction="filters.direction"
            @sort="setSort"
          >
            <template #before-row="{ row }">
              <row-alert
                v-if="rowAlert.alert.value.message && rowAlert.alert.value.rowId === row.id"
                :message="rowAlert.alert.value.message"
                :type="rowAlert.alert.value.type"
                :alert-key="rowAlert.alert.value.id"
                @dismiss="rowAlert.clear()"
              />
            </template>
            <template #cell:invoiceNumber="{ row }">
              <a
                v-if="row.pdfUrl"
                :href="row.pdfUrl"
                target="_blank"
                rel="noopener"
                class="text-primary"
              >#{{ row.invoiceNumber }}</a>
              <span v-else-if="generatingPdfIds.has(row.id)" class="d-inline-flex align-items-center text-muted" style="gap:5px">
                <span class="incoming-pdf-spinner" />#{{ row.invoiceNumber }}
              </span>
              <a
                v-else
                href="#"
                class="text-muted"
                @click.prevent="generateReportPdf(row.id)"
              >#{{ row.invoiceNumber }}</a>
            </template>
            <template #cell:amount="{ row }">
              <div v-if="editingId !== row.id" style="display: flex; width: 100%; justify-content: space-between; align-items: center">
                <span>{{ row.amount }}</span>
                <a href="#" class="icon-btn icon-btn--edit" title="Edit amount" @click.prevent="startEdit(row)" />
              </div>
              <div v-else class="input-append d-flex align-items-center">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control flex-grow-1"
                  v-model="editAmount"
                  @keyup.enter="saveAmount(row)"
                  @keyup.esc="cancelEdit"
                />
                <button class="btn btn-outline-primary flex-shrink-0 ml-2" @click="saveAmount(row)">Save</button>
              </div>
            </template>
          </grid-table>
        </div>

        <reports-totals :totals="totals" class="mt-3" />

        <pagination :total-pages="lastPage" :page="currentPage" :on-change="setPage" class="mt-3" />
      </template>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, getCurrentInstance, onMounted } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import GridTable from '@/components/GridTable'
import RowAlert from '@/components/RowAlert'
import useRowAlert from '@/composables/useRowAlert'
import Pagination from '@/components/Pagination'
import ReportsFilterBar from '@/components/Reports/ReportsFilterBar'
import ReportsTotals from '@/components/Reports/ReportsTotals'
import useReportsIncoming from '@/composables/reports/useReportsIncoming'
import useReportsFilter from '@/composables/reports/useReportsFilter'

const COMMIT_FILTERS = [
  { id: null, label: 'All' },
  { id: 'cash', label: 'Cash' },
  { id: 'direct', label: 'Direct deposits' },
  { id: 'cheque', label: 'Bank cheque' },
  { id: 'credit', label: 'Committee credit' },
  { id: 'eftpos', label: 'EFTPOS' }
]

const INCOMING_COLUMNS = [
  { key: 'date', label: 'Date received', sortable: true, primary: true, width: '1fr' },
  { key: 'invoiceNumber', label: 'Invoice number', sortable: true, mobile: 'label', width: '1fr' },
  { key: 'competitorName', label: 'Competitor name', sortable: true, mobile: 'label', width: '1.5fr' },
  { key: 'paymentType', label: 'Payment type', mobile: 'hide', width: '1fr' },
  { key: 'amount', label: 'Amount received', sortable: true, mobile: 'label', width: '1fr' }
]

export default defineComponent({
  name: 'ReportsIncoming',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    GridTable,
    RowAlert,
    Pagination,
    ReportsFilterBar,
    ReportsTotals
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const incoming = useReportsIncoming()
    const searchInput = ref('')
    const editingId = ref(null)
    const editAmount = ref('')
    // The result of saving an amount appears right above that row
    const rowAlert = useRowAlert()

    function startEdit(row) {
      editingId.value = row.id
      editAmount.value = row.amount != null ? row.amount : ''
    }

    function cancelEdit() {
      editingId.value = null
      editAmount.value = ''
    }

    async function saveAmount(row) {
      const result = await incoming.updateAmount(row.id, editAmount.value)
      editingId.value = null
      editAmount.value = ''
      if (result) {
        const type = result.success ? 'success' : (result.code === 'validation_error' ? 'danger' : 'warning')
        rowAlert.show(result.message, type, row.id)
      }
    }

    const { state: filterState, setYear, setDraft, initFromQuery } = useReportsFilter(proxy.$router, () => {
      incoming.filters.year = filterState.year
      incoming.filters.draft_id = filterState.draftId
      incoming.currentPage.value = 1
      incoming.fetch()
      incoming.fetchTotals()
    })

    function onYearChange(val) {
      setYear(val)
    }

    function onDraftChange(val) {
      setDraft(val)
    }

    onMounted(() => {
      initFromQuery(proxy.$route.query)
    })

    return {
      filterState,
      items: incoming.items,
      totals: incoming.totals,
      loading: incoming.loading,
      filters: incoming.filters,
      currentPage: incoming.currentPage,
      lastPage: incoming.lastPage,
      setCommit: incoming.setCommit,
      setSearch: incoming.setSearch,
      resetSearch: incoming.resetSearch,
      setSort: incoming.setSort,
      setPage: incoming.setPage,
      updateAmount: incoming.updateAmount,
      exportReport: incoming.exportReport,
      exportingFormat: incoming.exportingFormat,
      generateReportPdf: incoming.generateReportPdf,
      generatingPdfIds: incoming.generatingPdfIds,
      searchInput,
      editingId,
      editAmount,
      rowAlert,
      startEdit,
      cancelEdit,
      saveAmount,
      onYearChange,
      onDraftChange,
      commitFilters: COMMIT_FILTERS,
      incomingColumns: INCOMING_COLUMNS
    }
  }
})
</script>

<style scoped>
.incoming-pdf-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: incoming-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes incoming-spin {
  to { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// src/views/Reports/ReportsIncoming.vue